import { InspectionTemplateColumn } from "@/shared/models/Inspection";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, Button, Grid2, Typography } from "@mui/material";
import { useGroupDragReorder } from "../hooks/useGroupDragReorder";
import {
	buildGroups,
	groupNamesOf,
	settleGroupNames,
	splitGroups,
} from "../utils/columnGroups";
import { TemplateColumnRow } from "./TemplateColumnRow";

type Props = {
	columns: InspectionTemplateColumn[];
	disabled?: boolean;
	onChange: (columns: InspectionTemplateColumn[]) => void;
};

export const TemplateColumnsEditor = ({
	columns,
	disabled = false,
	onChange,
}: Props) => {
	const groups = buildGroups(columns);
	const split = splitGroups(columns);

	const { draggedGroup, handleProps, dropTargetProps } = useGroupDragReorder({
		groups,
		disabled,
		onChange,
	});

	const withChange = (
		index: number,
		changes: Partial<InspectionTemplateColumn>,
	) =>
		columns.map((column, position) =>
			position === index ? { ...column, ...changes } : column,
		);

	const updateColumn = (
		index: number,
		changes: Partial<InspectionTemplateColumn>,
	) => onChange(withChange(index, changes));

	// Picking a group from the list has to land the name and regroup in one update. Doing it as
	// two calls made the second one settle the columns as they were before the first, which threw
	// the pick away and left the field blank.
	const pickGroup = (index: number, groupName: string | null) =>
		onChange(settleGroupNames(withChange(index, { GroupName: groupName })));

	return (
		<Grid2 container spacing={1}>
			<Grid2 size={{ xs: 12 }}>
				<Typography variant="subtitle2" fontWeight={600}>
					Columns
				</Typography>
				{split.length > 0 && (
					<Typography variant="caption" color="warning.main" display="block">
						{`${split.join(", ")} ${
							split.length === 1 ? "is" : "are"
						} split across the sheet; the next change here puts each group back together.`}
					</Typography>
				)}
			</Grid2>

			{groups.map((group, groupIndex) => {
				const isDragged = draggedGroup === group;
				const rule = isDragged
					? "primary.main"
					: group.columns.length > 1
						? "divider"
						: "transparent";

				return (
					<Grid2
						key={`group-${groupIndex}`}
						container
						size={{ xs: 12 }}
						spacing={1}
						alignItems="center"
						wrap="nowrap"
						sx={{
							borderLeft: 2,
							borderColor: rule,
							borderRadius: 1,
							opacity: isDragged ? 0.4 : 1,
							backgroundColor: isDragged ? "action.hover" : "transparent",
						}}
						{...dropTargetProps(group)}
					>
						<Grid2 size="auto">
							<Box
								{...handleProps(group)}
								title={
									group.columns.length > 1
										? `Drag to reorder ${group.columns[0].GroupName} and its ${group.columns.length} columns`
										: "Drag to reorder"
								}
								sx={{
									display: "flex",
									color: "text.disabled",
									cursor: disabled ? "default" : "grab",
									"&:active": { cursor: disabled ? "default" : "grabbing" },
								}}
							>
								<DragIndicatorIcon fontSize="small" />
							</Box>
						</Grid2>
						<Grid2 container size="grow" spacing={1}>
							{group.columns.map((column, position) => {
								const index = columns.indexOf(column);

								return (
									<TemplateColumnRow
										key={`row-${position}`}
										column={column}
										groupNames={groupNamesOf(columns, index)}
										disabled={disabled}
										onChange={(changes) => updateColumn(index, changes)}
										onGroupPicked={(groupName) => pickGroup(index, groupName)}
										onGroupSettled={() => onChange(settleGroupNames(columns))}
										onRemove={() =>
											onChange(
												columns.filter(
													(_column, position) => position !== index,
												),
											)
										}
									/>
								);
							})}
						</Grid2>
					</Grid2>
				);
			})}

			<Grid2 size={{ xs: 12 }}>
				<Button
					startIcon={<AddIcon />}
					disabled={disabled}
					onClick={() =>
						onChange([
							...columns,
							{
								ColumnId: null,
								TemplateId: null,
								GroupName: null,
								ColumnLabel: "",
								ValueTypeCode: "G",
								IsActive: true,
								SortOrder: columns.length + 1,
							},
						])
					}
				>
					Add Column
				</Button>
			</Grid2>
		</Grid2>
	);
};
